import { MappedEntity, PreliminaryPrice, SnakeMeta } from '@api';
import { ChartAPIReturn, SeriesType, TimeSeriesItem } from '@chart';
import { isPriceSnake } from '@helpers';
import { PriceDisplayReturn } from '@terminal/components/price-display-view/helpers/price-display.types';
import { formatPriceDisplay } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import {
  getLivePriceDataKey,
  useLivePrice,
} from '@terminal/hooks/use-live-price';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { globalPriceFormatter } from '@terminal/utils/asset-price/assetPrice';
import { priceSuffix } from '@terminal/utils/currency/currency';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LastClosePrice,
  LastClosePriceProps,
} from '../last-close-price/LastClosePrice';
import { LegendLivePrice } from '../legend-live-price/LegendLivePrice';
import { PriceIcon } from '../legend-price-icons/LegendPriceIcons';

export interface LegendPriceProps {
  entity: MappedEntity;
  snakeMeta: SnakeMeta;
  preliminaryPrice?: PreliminaryPrice;
  isChartHovered: boolean;
  legendColor: string;
  isPrimary: boolean;
  selectedSeriesType?: SeriesType;
  ts: TimeSeriesItem[];
  hoverData?: LastClosePriceProps['hoverData'];
  chartApiRef?: React.RefObject<ChartAPIReturn | null>;
}

export const legendIconMapping = (price: PriceDisplayReturn): PriceIcon[] => {
  return [
    {
      iconName: 'SunRise',
      tooltipLabel: 'chart:preMarketTooltip',
      color: 'var(--icon-primary)',
      shouldShow: price.primary.marketHours.isPreMarket,
    },
    {
      iconName: 'Sun',
      tooltipLabel: 'chart:marketOpenTooltip',
      color: 'var(--viz-bullish)',
      shouldShow: price.primary.marketHours.isMarketHours,
    },
    {
      iconName: 'Moon',
      tooltipLabel: 'chart:marketClosedTooltip',
      color: 'var(--icon-soft)',
      shouldShow: price.primary.marketHours.isAfterMarket,
    },
  ];
};

export const LegendPrice = ({
  entity,
  snakeMeta,
  preliminaryPrice,
  isChartHovered,
  legendColor,
  isPrimary,
  selectedSeriesType,
  ts,
  hoverData,
  chartApiRef,
}: LegendPriceProps) => {
  const priceFormatter = useCallback(
    (price: number) =>
      globalPriceFormatter({
        entity,
        price,
        priceSuffix: snakeMeta
          ? priceSuffix(snakeMeta.display_format, entity.currency)
          : '',
        shouldShowCurrency: false,
      }),
    [entity]
  );

  const { t } = useTranslation('chart');
  const {
    ticker,
    sub_class,
    primary_method,
    subscribable_ticker,
    trading_country,
  } = entity;
  // Only subscribe to live prices for price snakes
  const isLivePriceSnake = isPriceSnake(snakeMeta.snake);
  const priceEntities = isLivePriceSnake
    ? [
        {
          entity: {
            ticker,
            sub_class,
            primary_method,
            subscribable_ticker,
            trading_country,
            exchange: entity.exchange.code,
          },
          lastKnownPrice: snakeMeta.last_value,
          priceBefore: snakeMeta.before_last_value,
        },
      ]
    : [];

  const livePriceMap = useLivePrice(priceEntities);
  const livePriceDataKey = getLivePriceDataKey(entity) ?? '';
  const livePriceInfo = livePriceMap[livePriceDataKey];

  const price = formatPriceDisplay({
    exchange: entity.exchange.code,
    livePriceInfo,
    preliminaryPrice,
    meta: snakeMeta,
    isHovered: isChartHovered,
    t,
  });

  const isLiveCandlesEnabled = useFlags(state =>
    state.getFeatureFlagState('live_candles_feature_flag')
  );

  useEffect(() => {
    const isOHLCSeries =
      selectedSeriesType &&
      ['bars', 'candlestick'].includes(selectedSeriesType);
    const isLineMode = !isOHLCSeries;
    const isOneMinute =
      chartApiRef?.current?.getBaseData?.()?.resampleInterval === '1m';

    if (
      chartApiRef?.current &&
      isPrimary &&
      isLineMode &&
      isOneMinute &&
      livePriceInfo &&
      livePriceInfo.price !== undefined &&
      livePriceInfo.time &&
      isLiveCandlesEnabled
    ) {
      chartApiRef.current.updateLastPrice(
        livePriceInfo.price,
        livePriceInfo.time,
        entity.default_snake
      );
    }
  }, [
    livePriceInfo?.price,
    livePriceInfo?.time,
    chartApiRef,
    isPrimary,
    chartApiRef?.current?.getBaseData?.()?.resampleInterval,
    selectedSeriesType,
    isLiveCandlesEnabled,
    // keep entity context stable changes out
  ]);

  if (!snakeMeta || !ts.at(-1)) {
    return null;
  }

  const isOHLCSeries =
    selectedSeriesType && ['bars', 'candlestick'].includes(selectedSeriesType);
  const isOHLCPrice = isOHLCSeries && isPrimary;

  //TODO will add back in full live price when we have figured out the OHLC live legend
  const shouldShowLivePrice =
    isLivePriceSnake &&
    !isOHLCPrice &&
    !hoverData &&
    chartApiRef?.current?.getBaseData?.()?.resampleInterval === '1m' &&
    isLiveCandlesEnabled;

  if (shouldShowLivePrice) {
    return (
      <LegendLivePrice
        iconMapping={legendIconMapping(price)}
        price={price}
        isPrimary={isPrimary}
        priceFormatter={priceFormatter}
        legendColor={legendColor}
      />
    );
  }

  return (
    <LastClosePrice
      iconMapping={legendIconMapping(price)}
      price={price}
      entity={entity}
      snakeMeta={snakeMeta}
      legendColor={legendColor}
      isPrimary={isPrimary}
      priceFormatter={priceFormatter}
      seriesType={selectedSeriesType}
      ts={ts}
      hoverData={hoverData}
      chartApiRef={chartApiRef}
      livePriceInfo={livePriceInfo}
    />
  );
};
